import { useState, useCallback, useEffect, KeyboardEvent } from 'react';
import { Button, TextField, Box, FormControlLabel, Switch } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useEditorBubbleMenu } from 'components/EditorBubbleMenu/context';
import { isValidUrl } from 'utility/functions';
import { commonStyles } from './styles';
import { useTranslation } from 'react-i18next';

const LinkAssetSearch = () => {
  const { editor, bubbleCleanup } = useEditorBubbleMenu();
  const { t } = useTranslation();
  const [error, setError] = useState<boolean>(false);
  const [newTab, setNewTab] = useState(() => {
    if (!editor) return true;
    const target = editor.getAttributes('link').target;
    // Default to true for new links (no existing target), otherwise reflect the current setting
    return target ? target === '_blank' : true;
  });
  const [searchState, setSearchState] = useState({
    link: '',
  });

  const updateLink = useCallback(
    (url: string | null) => {
      if (!editor) return;

      if (!url) {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
      } else {
        if (isValidUrl(url)) {
          setError(false);
          editor
            .chain()
            .focus()
            .extendMarkRange('link')
            .setLink({
              href: url,
              target: newTab ? '_blank' : '_self', // Set target based on newTab state
            })
            .run();
        } else {
          setError(true);
          return;
        }
      }
      bubbleCleanup();
    },
    [editor, bubbleCleanup, newTab],
  );

  // Shared function for both button click and Enter key
  const handleApplyLink = useCallback(() => {
    if (searchState.link.trim()) {
      updateLink(searchState.link);
    }
  }, [searchState.link, updateLink]);

  const handleSearchChange = useCallback((value: string) => {
    setError(false);
    setSearchState((prev) => ({
      ...prev,
      link: value,
    }));
  }, []);

  // Handle Enter key press in the search field
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleApplyLink();
      }
    },
    [handleApplyLink],
  );

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setNewTab(event.target.checked);
  }, []);

  useEffect(() => {
    if (editor) {
      const currentLink = editor.getAttributes('link').href || '';
      setSearchState((prev) => ({ ...prev, link: currentLink }));
    }
  }, [editor]);

  // Is the field non-empty
  const hasContent = Boolean(searchState.link.trim());

  if (!editor) return null;
  return (
    <Box
      data-testid="bubble-menu-link-asset-search"
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', height: 34 }}>
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            pl: 0.5,
            width: '100%',
            border: '1px solid',
            borderColor: theme.palette.divider,
            height: '100%',
          })}
        >
          <TextField
            data-testid="bubble-menu-link-asset-search-input"
            autoFocus
            required
            variant="outlined"
            placeholder={t('blox.general.search_url')}
            value={searchState.link}
            onChange={(e) => handleSearchChange(e.target.value)}
            onKeyDown={handleKeyDown}
            sx={(theme) => ({
              ...commonStyles.searchField,
              p: 1 / 2,
              mx: -1 / 2,
              height: '100%',
              '& .MuiInputBase-input': {
                color: error ? theme.palette.error.main : 'inherit',
              },
              '& .MuiFormHelperText-root': {
                fontSize: 12,
              },
            })}
            error={error}
            helperText={error ? t('blox.general.invalid_url_help') : ''}
          />
          <Button
            onClick={handleApplyLink}
            disabled={!hasContent}
            sx={{ height: '100%' }}
          >
            {t('blox.actions.apply')}
          </Button>
        </Box>
      </Box>

      <Box
        data-testid="bubble-menu-link-asset-search-options"
        sx={{ pl: 0.5 }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={newTab}
              onChange={handleChange}
              size="small"
              slotProps={{
                input: { 'aria-label': 'Open in new tab' },
              }}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <span>{t('blox.actions.open_in_new_tab')}</span>
              <OpenInNewIcon style={{ height: 16, width: 16 }} />
            </Box>
          }
        />
      </Box>
    </Box>
  );
};
export default LinkAssetSearch;
